import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { BluechatLoad } from '../../shared/bluechat-load.model';
import { BluechatDelivery } from '../../shared/bluechat-delivery.model';
import { LoadManagementSearchCarrierDto, LoadManagementSearchRegionDto } from '../../shared/load-management-search-response';

@Component({
  selector: 'app-bluechat-list',
  templateUrl: './bluechat-list.component.html',
  styleUrls: ['./bluechat-list.component.scss']
})
export class BluechatListComponent implements OnChanges {

  @Input() public bluechatLoads: Array<BluechatLoad> = [];
  @Input() public selectedLoads: Array<BluechatLoad> = [];
  @Input() public showByCarrier                      = false;
  @Input() public userCanCreateNote                      = false;
  @Input() public carriers: { [carrierCode: string]: LoadManagementSearchCarrierDto } = {};
  @Input() public regions: { [regionId: string]: LoadManagementSearchRegionDto } = {};
  @Output() public selectedLoadsChange               = new EventEmitter<Array<BluechatLoad>>();
  @Output() public createNote                        = new EventEmitter<BluechatLoad>();
  @Output() public openDetail                        = new EventEmitter<BluechatLoad>();

  public _selectedCarriers: { [carrierCode: string]: boolean }             = {};
  public _selectedLoads: { [loadNumber: string]: Array<BluechatDelivery> } = {};
  public groupedBluechatLoads: { [regionId: string]: { [carrierCode: string]: Array<BluechatLoad> } } = {};

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('bluechatLoads') && !changes['bluechatLoads'].isFirstChange()) {
      this.resetSelection();
      if (this.showByCarrier) {
        this.groupBluechatLoads();
      }
    }

    if (changes.hasOwnProperty('selectedLoads') && !changes['selectedLoads'].isFirstChange()) {
      this.handleSelectedLoadsUpdate();
    }
  }

  public selectCarrier(carrierId: string) {
    if (this._selectedCarriers[carrierId]) {
      this._selectedCarriers[carrierId] = false;
      this.bluechatLoads
        .filter(bluechatLoad => bluechatLoad.blueyonderCarrierCode === carrierId)
        .forEach(bluechatLoad => delete this._selectedLoads[bluechatLoad.loadNumber]);
    } else {
      this._selectedCarriers[carrierId] = true;
      this.bluechatLoads
        .filter(bluechatLoad => bluechatLoad.blueyonderCarrierCode === carrierId)
        .forEach(bluechatLoad => this._selectedLoads[bluechatLoad.loadNumber] = bluechatLoad.deliveries);
    }
    this.updateSelectedLoads();
  }

  public selectDeliveries(loadNumber: string, deliveries: Array<BluechatDelivery>) {
    if (deliveries.length > 0) {
      this._selectedLoads[loadNumber] = deliveries;
    } else {
      delete this._selectedLoads[loadNumber];
    }
    this.updateSelectedLoads();
  }

  private groupBluechatLoads() {
    const result = {};
    for (const bluechatLoad of this.bluechatLoads) {
      const regionId    = this.carriers[bluechatLoad.blueyonderCarrierCode].region.id;
      const blueyonderCarrierCode = bluechatLoad.blueyonderCarrierCode;

      if (result.hasOwnProperty(regionId)) {
        const region = result[regionId];
        if (region.hasOwnProperty(blueyonderCarrierCode)) {
          result[regionId][blueyonderCarrierCode].push(bluechatLoad);
        } else {
          result[regionId][blueyonderCarrierCode] = [bluechatLoad];
        }
      } else {
        result[regionId] = { [blueyonderCarrierCode]: [bluechatLoad] };
      }
    }

    this.groupedBluechatLoads = result;
  }

  private resetSelection() {
    this._selectedCarriers = {};
    this._selectedLoads    = {};
  }

  private handleSelectedLoadsUpdate() {
    this.resetSelection();
    this.selectedLoads.forEach(bluechatLoad => {
      this._selectedLoads[bluechatLoad.loadNumber]     = bluechatLoad.deliveries;
      this._selectedCarriers[bluechatLoad.blueyonderCarrierCode] = this.isCarrierSelected(bluechatLoad.blueyonderCarrierCode);
    });
  }

  private updateSelectedLoads() {
    const selectedLoads = this.bluechatLoads
      .filter(bluechatLoad => this._selectedLoads.hasOwnProperty(bluechatLoad.loadNumber))
      .map(bluechatLoad => {
        const selectedBluechatLoad      = { ...bluechatLoad };
        selectedBluechatLoad.deliveries = this._selectedLoads[bluechatLoad.loadNumber];
        return selectedBluechatLoad;
      });
    this.selectedLoadsChange.emit(selectedLoads);
  }

  private isCarrierSelected(carrierCode: string) {
    const carrierRegionId = this.carriers[carrierCode].region.id;
    if (!this.groupedBluechatLoads.hasOwnProperty(carrierRegionId)
      || !this.groupedBluechatLoads[carrierRegionId].hasOwnProperty(carrierCode)) {
      return false;
    }
    return this.groupedBluechatLoads[carrierRegionId][carrierCode]
      .every(bluechatLoad => this._selectedLoads[bluechatLoad.loadNumber]?.length === bluechatLoad.deliveries.length);
  }
}
